/**
 * MSSQL built-in SYSTEM/METADATA + hashing function signatures — one family
 * slice of the static catalog composed by `mssqlBuiltinRoutines.ts` (see
 * that file's header for why this exists and the precedence rule vs. user
 * routines).
 *
 * Zero-arg system functions (`NEWID()`, `HOST_NAME()`, ...) and niladic
 * keywords (`CURRENT_USER`, `SESSION_USER`, `SYSTEM_USER` — no parens ever)
 * are excluded — see `MSSQL_EXCLUDED_FROM_SIGNATURE_HELP` in the barrel.
 */

import type { RoutineMetadata, RoutineParameter } from '../../../../types/electron';

type Entry = RoutineMetadata & { documentation?: string };

function p(name: string, dataType: string): RoutineParameter {
  return { name, dataType };
}

function opt(name: string, dataType: string): RoutineParameter {
  return { name: `[${name}]`, dataType };
}

function variadic(dataType: string): RoutineParameter {
  return { name: '...', dataType };
}

function entry(name: string, parameters: RoutineParameter[], documentation?: string): Entry {
  return { schema: '', name, parameters, ...(documentation ? { documentation } : {}) };
}

export const MSSQL_SYSTEM_ROUTINES: ReadonlyMap<string, Entry> = new Map([
  // --- System / metadata ---
  ['db_id', entry('DB_ID', [opt('database_name', 'sysname')], 'Return the database ID, or the current database ID if omitted.')],
  ['db_name', entry('DB_NAME', [opt('database_id', 'int')], 'Return the database name, or the current database name if omitted.')],
  ['object_id', entry('OBJECT_ID', [p("'object_name'", 'nvarchar'), opt("'object_type'", 'varchar')], 'Return the database object identification number for a schema-scoped object.')],
  ['object_name', entry('OBJECT_NAME', [p('object_id', 'int'), opt('database_id', 'int')], 'Return the database object name for a schema-scoped object identification number.')],
  ['object_schema_name', entry('OBJECT_SCHEMA_NAME', [p('object_id', 'int'), opt('database_id', 'int')], 'Return the schema name of a schema-scoped object.')],
  ['schema_id', entry('SCHEMA_ID', [opt('schema_name', 'sysname')], 'Return the schema ID, or the current schema ID if omitted.')],
  ['schema_name', entry('SCHEMA_NAME', [opt('schema_id', 'int')], 'Return the schema name, or the current schema name if omitted.')],
  ['suser_name', entry('SUSER_NAME', [opt('server_user_id', 'int')], "Return a login's name from its security identification number (SID).")],
  ['suser_sname', entry('SUSER_SNAME', [opt('server_user_sid', 'varbinary')], "Return a login's name from its security identifier (SID).")],
  ['user_name', entry('USER_NAME', [opt('id', 'int')], 'Return a database user name from its identification number.')],
  // --- Hashing / checksums ---
  ['checksum', entry('CHECKSUM', [p('* | expression', 'any'), variadic('any')], 'Return a checksum computed over a list of columns/expressions.')],
  ['binary_checksum', entry('BINARY_CHECKSUM', [p('* | expression', 'any'), variadic('any')], 'Like CHECKSUM but sensitive to the exact binary representation.')],
  ['hashbytes', entry('HASHBYTES', [p('algorithm', 'varchar'), p('input', 'varbinary')], "Hash input with the given algorithm ('MD5', 'SHA2_256', 'SHA2_512', ...).")],
]);
